import type { ComponentProps, PropsWithChildren, ReactNode, Ref } from 'react';

import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Checkmark, ChevronSmallRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import clsx from 'clsx';

export interface DropdownMenuItem {
  label: ReactNode;
  id: string;
  onClick?: () => void;
  disabled?: boolean;
  subMenu?: (DropdownMenuItem | undefined)[];
}

export const DropdownMenu = ({
  items,
  children,
  side = 'bottom',
  trigger,
  collisionPadding = 16,
  align = 'start',
  sticky = 'always',
  open,
  onOpenChange,
  ...props
}: {
  items?: (DropdownMenuItem | undefined)[];
  trigger: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} & ComponentProps<typeof Dropdown.Content>) => {
  return (
    <Dropdown.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content
          className={clsx(
            'rounded-sm z-50 flex flex-col gap-4 bg-white p-4 shadow-lg',
            {
              'animate-fade-in-down-short': side === 'bottom',
              'animate-fade-in-up-short': side === 'top',
            }
          )}
          sideOffset={4}
          side={side}
          collisionPadding={collisionPadding}
          align={align}
          sticky={sticky}
          {...props}
        >
          {items ? renderMenuItems(items) : children}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const renderMenuItems = (items?: (DropdownMenuItem | undefined)[]) =>
  items?.map((item) => {
    if (!item) return null;

    if (!item.subMenu) {
      return (
        <DropdownMenuItem
          key={item.id}
          onClick={item.onClick}
          disabled={item.disabled}
        >
          {item.label}
        </DropdownMenuItem>
      );
    }
    return (
      <Dropdown.Sub key={item.id}>
        <Dropdown.SubTrigger
          className={clsx(
            'rounded-sm px-16 py-12 interface-md hover:outline-hidden',
            {
              'cursor-pointer hover:bg-neutral-light-3': !item.disabled,
              'opacity-40': item.disabled,
            }
          )}
        >
          {item.label}
          <ChevronSmallRight className="ml-8 inline" />
        </Dropdown.SubTrigger>
        <Dropdown.Portal>
          <Dropdown.SubContent
            className="rounded-sm flex flex-col gap-4 bg-white p-8 shadow-lg"
            sideOffset={2}
            alignOffset={-5}
          >
            {renderMenuItems(item.subMenu)}
          </Dropdown.SubContent>
        </Dropdown.Portal>
      </Dropdown.Sub>
    );
  });

export function DropdownMenuItem({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof Dropdown.Item> & { ref?: Ref<HTMLDivElement> }) {
  return (
    <Dropdown.Item
      ref={ref}
      className={clsx(
        'rounded-sm px-[14px] py-[10px] interface-md text-14 outline-hidden transition-all hover:outline-hidden focus:outline',
        {
          'cursor-pointer hover:bg-neutral-light-3': !props.disabled,
          'cursor-not-allowed opacity-40': props.disabled,
        },
        className
      )}
      onClick={props.onClick}
      {...props}
    >
      {children}
    </Dropdown.Item>
  );
}

export const DropdownMenuSeparator = () => (
  <Dropdown.Separator className="mx-16 my-12 h-[1px] border-neutral-light-4" />
);

export const DropdownMenuLabel = (props: PropsWithChildren) => (
  <Dropdown.DropdownMenuLabel className="px-[14px] py-4">
    <Typography variant="interface-xs" leadingNone className="text-muted">
      {props.children}
    </Typography>
  </Dropdown.DropdownMenuLabel>
);

export function DropdownMenuCheckboxItem({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof Dropdown.CheckboxItem> & {
  ref?: Ref<HTMLDivElement>;
}) {
  return (
    <Dropdown.CheckboxItem
      ref={ref}
      className={clsx(
        'rounded-sm flex items-center gap-8 py-[10px] pr-16 pl-40 interface-md text-14 hover:outline-hidden',
        {
          'cursor-pointer hover:bg-neutral-light-3': !props.disabled,
          'cursor-not-allowed opacity-40': props.disabled,
        },
        className
      )}
      onClick={props.onClick}
      {...props}
    >
      <Dropdown.ItemIndicator className="absolute left-20">
        <Checkmark size={16} />
      </Dropdown.ItemIndicator>
      <span>{children}</span>
    </Dropdown.CheckboxItem>
  );
}
